import { useCallback, useMemo, useState } from 'react';

import { TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerRenterContactWebApiHandler_getTenants } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyTenantsCard from './components/EmptyTenantsCard';

import {
  CS_EVENT_TRIGGER_LOCATION,
  emitTenantsModuleAddTenantClickEvent,
  emitTenantsModuleSaveClickEvent,
  TENANTS_MODULE_ADD_TENANT_CLICK_EVENT_ID,
} from '../../../../analytics/events/tenantDirectory';
import { client } from '../../../../http/zgApiClient';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import TenantAccordionList from './TenantAccordionList';

type Props = {
  listingAlias: string;
  isArchived: boolean;
};

const TenantsModule = ({ listingAlias, isArchived }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);

  const {
    data: getTenantsData,
    isFetching,
    isPending,
  } = useRentalManagerRenterContactWebApiHandler_getTenants({ listingAlias }, { enabled: Boolean(listingAlias) });

  const tenants = getTenantsData?.tenants ?? [];
  const isEmptyTenants = tenants.length === 0;

  const invalidateGetTenantsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
    });
  };

  const handleAddTenantClick = useCallback(() => {
    setIsModalTenantSelectOpen(true);
    if (user) {
      emitTenantsModuleAddTenantClickEvent({
        currentProperty: { listingAlias },
        user,
        triggerLocation: CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
      });
    }
  }, [listingAlias, user]);

  const handleDeleteTenancyPeriodSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">Tenancy removed</Toast>);
  };

  const handleDeleteTenantSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">Tenant removed</Toast>);
  };

  const handleDeleteTenantError = ({ message }: { message: string }) => {
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  const handleEditTenantSuccess = () => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">Tenant details updated</Toast>);
  };

  const handleLeaseTermActionSuccess = ({ isEditMode }: { isEditMode: boolean }) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{isEditMode ? 'Tenancy updated' : 'Tenancy created'}</Toast>);
  };

  const handleRequestProofSuccess = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{message}</Toast>);
  };

  const handleRequestProofError = (message: string) => {
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  const renderCTA = useMemo(() => {
    return (
      <TextButton
        tone="brand"
        icon={<IconPlusCircleFilled />}
        data-eventid={TENANTS_MODULE_ADD_TENANT_CLICK_EVENT_ID}
        onClick={handleAddTenantClick}
      >
        Add tenant
      </TextButton>
    );
  }, [handleAddTenantClick]);

  return (
    <>
      <ConnectedModuleCard
        data-testid="TenantsModule"
        title="Tenants"
        ctaComponent={!isPending && !isArchived && renderCTA}
        ctaFontType="body-sm"
        justifyHeadingContent={'center'}
        isLoading={isPending || isFetching}
      >
        {isPending === false && isFetching === false && (
          <>
            {isEmptyTenants ? (
              <EmptyTenantsCard />
            ) : (
              <TenantAccordionList
                listingAlias={listingAlias}
                tenants={tenants}
                isArchived={isArchived}
                onDeleteTenancyPeriodSuccess={handleDeleteTenancyPeriodSuccess}
                onDeleteTenantSuccess={handleDeleteTenantSuccess}
                onDeleteTenantError={handleDeleteTenantError}
                onEditTenantSuccess={handleEditTenantSuccess}
                onLeaseTermActionSuccess={handleLeaseTermActionSuccess}
                onRequestProofSuccess={handleRequestProofSuccess}
                onRequestProofError={handleRequestProofError}
              />
            )}
          </>
        )}
      </ConnectedModuleCard>

      {!isArchived && (
        <ModalTenantSelect
          client={client}
          listingAlias={listingAlias}
          isOpen={isModalTenantSelectOpen}
          onAddRenterSuccess={() => {
            if (user) {
              emitTenantsModuleSaveClickEvent({
                triggerLocation: CS_EVENT_TRIGGER_LOCATION.ZRM_PROPERTY_OVERVIEW,
                currentProperty: { listingAlias },
                user,
              });
            }
          }}
          queryClient={queryClient}
          onClose={() => setIsModalTenantSelectOpen(false)}
          onPrimaryCTAClick={() => {
            invalidateGetTenantsQuery();
            setIsModalTenantSelectOpen(false);
          }}
        />
      )}
    </>
  );
};

export default TenantsModule;
